import { useState, useCallback } from 'react';
import { Skeleton } from '@/shared/ui/skeleton';
import { Button } from '@/shared/ui/button';
import { ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Pagination } from '@/shared/widgets/pagination';


// Import types from the types file
import {
  PackPresetStats,
  CreatorPresetStats,
  PresetStatsData,
} from '../model/presetStats.types';
import { Select } from '@/shared/widgets/select';
import { SortOption } from '../model/analytics.types';

interface AnalyticsPresetTableProps {
  packData: PackPresetStats[];
  packMeta?: PresetStatsData['meta'];
  creatorData: CreatorPresetStats[];
  creatorMeta?: PresetStatsData['meta'];
  loadingPack: boolean;
  loadingCreator: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  // Pagination props
  currentPage?: number;
  limit?: number;
  totalPages?: number;
  setCurrentPage?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  activeTab?: 'pack' | 'creator';
  setActiveTab?: (tab: 'pack' | 'creator') => void;
  sortBy?: SortOption;
  setSortBy?: (sortBy: SortOption) => void;
  sortOrder?: 'asc' | 'desc';
  setSortOrder?: (sortOrder: 'asc' | 'desc') => void;
}

export const TableSkeleton = ({ rows = 5 }: { rows?: number }) => {
  return (
    <div className="mb-8 h-[600px] animate-pulse">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <Skeleton className="h-8 w-64 bg-[#232323] rounded" />
        <div className="flex space-x-4">
          <Skeleton className="h-10 w-24 bg-[#3d3d3d] rounded" />
          <Skeleton className="h-10 w-24 bg-[#3d3d3d] rounded" />
          <Skeleton className="h-10 w-10 rounded-full bg-[#3d3d3d]" />
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto border border-[#2c2c2c] rounded">
        {/* Table header */}
        <div className="grid grid-cols-6 gap-4 p-4 bg-[#1f1f1f] border-b border-[#2c2c2c]">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full bg-[#2c2c2c] rounded" />
          ))}
        </div>

        {/* Table body */}
        <div>
          {Array.from({ length: rows }).map((_, i) => (
            <div
              key={i}
              className={`grid grid-cols-6 gap-4 p-4 border-b border-[#2c2c2c]`}
            >
              {Array.from({ length: 6 }).map((_, j) => (
                <Skeleton
                  key={j}
                  className="h-6 w-full bg-[#23232340] rounded"
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export function AnalyticsPresetTable(props: AnalyticsPresetTableProps) {
  const {
    packData = [],
    packMeta,
    creatorData = [],
    creatorMeta,
    loadingPack,
    loadingCreator,
    error,
    onRefresh,
    currentPage = 1,
    totalPages = 1,
    setCurrentPage,
    onPageChange,
    activeTab = 'creator',
    setActiveTab,
    onPageSizeChange,
    limit = 20,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  } = props;
  const [expandedPacks, setExpandedPacks] = useState<Record<string, boolean>>({});
  const [expandedCreators, setExpandedCreators] = useState<Record<string, boolean>>({});
  const isLoading = loadingPack || loadingCreator;

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const togglePack = (packName: string) => {
    setExpandedPacks(prev => ({
      ...prev,
      [packName]: !prev[packName]
    }));
  };

  const toggleCreator = (creatorName: string) => {
    setExpandedCreators(prev => ({
      ...prev,
      [creatorName]: !prev[creatorName]
    }));
  };

  const handlePageChange = (page: number) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
    if (onPageChange) {
      onPageChange(page);
    }
  };

  if (isLoading && (activeTab === 'pack' ? packData.length === 0 : creatorData.length === 0)) {
    return (
      <TableSkeleton />
    );
  }
  const byPackSortOptions = [
    { value: 'preset', label: 'Ordenar por Presets' },
    { value: 'pack', label: 'Ordenar por Packs' },
    { value: 'paidUses', label: 'Ordenar por Usos Pagos' },
    { value: 'freeUses', label: 'Ordenar por Usos Gratuitos' },
  ]
  const byCreatorSortOptions = [
    { value: 'creator', label: 'Ordenar por Creators' },
    { value: 'paidUses', label: 'Ordenar por Usos Pagos' },
    { value: 'freeUses', label: 'Ordenar por Usos Gratuitos' },
    { value: 'totalClicks', label: 'Ordenar por Total de clicks no link' },

  ]

  const currentMeta = activeTab === 'pack' ? packMeta : creatorMeta;
  const totalItemsValue = Number(currentMeta?.totalItems ?? 0);
  const totalPaidValue = activeTab === 'pack' ? packMeta?.totalPaid : creatorMeta?.totalPaid;
  const totalFreeValue = activeTab === 'pack' ? packMeta?.totalFree : creatorMeta?.totalFree;
  const totalClicksValue = activeTab === 'pack' ? packMeta?.totalClicks : creatorMeta?.totalClicks;
  const numericTotalPaid = Number(totalPaidValue ?? 0);
  const numericTotalFree = Number(totalFreeValue ?? 0);
  const numericTotalClicks = Number(totalClicksValue ?? 0);

  const summaryStats = [
    {
      label: `Total de ${activeTab === 'pack' ? 'Packs' : 'Creators'}`,
      value: totalItemsValue,
      color: 'text-white',
    },
    {
      label: 'Usos Pagos',
      value: numericTotalPaid,
      color: 'text-green-400',
    },
    {
      label: 'Usos Gratuitos',
      value: numericTotalFree,
      color: 'text-orange-400',
    },
    {
      label: 'Total de Usos',
      value: numericTotalPaid + numericTotalFree,
      color: 'text-blue-400',
    },
  ];

  if (totalClicksValue !== undefined && totalClicksValue !== null) {
    summaryStats.push({
      label: 'Total de Cliques',
      value: numericTotalClicks,
      color: 'text-purple-400',
    });
  }

  const summaryGridColumns = summaryStats.length >= 5 ? 'md:grid-cols-5' : 'md:grid-cols-4';
  const formatNumber = (value?: number | string) => {
    const numericValue = Number(value ?? 0);
    return Number.isNaN(numericValue) ? '0' : numericValue.toLocaleString('pt-BR');
  };

  if (error) {
    return (
      <div className="mb-8">
        <Alert variant="destructive" className="border-red-500/20 bg-red-900/20">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar dados</AlertTitle>
          <AlertDescription>
            {error.message || 'Ocorreu um erro ao carregar as estatísticas de presets.'}
            <Button
              variant="outline"
              size="sm"
              className="mt-2 bg-neutral-800 border-white/10 hover:bg-neutral-700"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Header Section */}
      <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Filter className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                Estatísticas de Presets
              </h2>
              <p className="text-sm text-white/60 mt-1">
                Análise detalhada de performance por pack e creator
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Header with Dropdown */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Dados Detalhados
        </h3>
        <Select onChange={(value) => {
          setActiveTab(value as 'pack' | 'creator')
          setCurrentPage(1)
        }} value={activeTab}
          data={[
            { value: 'pack', label: '📦 Por Pack' },
            { value: 'creator', label: '👤 Por Creator' },
          ]}
        />
        <Select onChange={(value) => {
          setSortBy(value as SortOption)
          setCurrentPage(1)
        }} value={sortBy}
          data={activeTab === 'pack' ? byPackSortOptions : byCreatorSortOptions}
        />
        <Select onChange={(value) => {
          setSortOrder(value as 'asc' | 'desc')
          setCurrentPage(1)
        }} value={sortOrder}
          data={[
            { value: 'asc', label: 'Ascendente' },
            { value: 'desc', label: 'Descendente' },
          ]}
        />
      </div>

      {/* Stats Summary */}
      <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4 mb-4">
        <div className={`grid grid-cols-1 ${summaryGridColumns} gap-4`}>
          {summaryStats.map(({ label, value, color }, i) => {
            const formattedValue = formatNumber(value);

            return (
              <div key={i} className="text-center">
                <div className={`text-2xl font-bold ${color}`}>
                  {formattedValue}
                </div>
                <div className="text-sm text-white/60">{label}</div>
              </div>
            );
          })}
        </div>
      </div>


      {/* Pack View */}
      {activeTab === 'pack' && (
        <div className="relative rounded-xl border border-white/10 overflow-hidden bg-neutral-900">
          {packMeta && (
            <div className="p-4 bg-neutral-800 text-sm text-white/60 border-b border-white/10">
              {[
                `Mostrando ${formatNumber(packData.length)} de ${formatNumber(packMeta.totalItems)} itens`,
                `${formatNumber(packMeta.totalPaid)} usos pagos`,
                `${formatNumber(packMeta.totalFree)} usos gratuitos`,
                `${formatNumber(packMeta.paidPercentage)}% taxa de conversão`,
                packMeta.totalClicks !== undefined && packMeta.totalClicks !== null
                  ? `${formatNumber(packMeta.totalClicks)} cliques`
                  : null,
              ].filter(Boolean).join(' • ')}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[768px] table-fixed">
              <thead className="bg-neutral-800">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-white/80 w-12"></th>
                  <th className="p-3 text-left text-sm font-medium text-white/80">Preset</th>
                  <th className="p-3 text-left text-sm font-medium text-white/80">Pack</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Totais</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Pagos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Gratuitos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80 pr-6">Taxa de Conversão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {packData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-3 text-center text-sm text-white/60">
                      Nenhum dado encontrado
                    </td>
                  </tr>
                )}
                {packData.map((item) => {
                  const isExpanded = expandedPacks[item.preset] || false;

                  const totalUses = item.totalUses || (item.paidUses + item.freeUses);
                  const paidPercentage = item.paidPercentage || (totalUses > 0
                    ? Math.round((item.paidUses / totalUses) * 100)
                    : 0);


                  return (
                    <>
                      <tr key={item.preset} className="hover:bg-neutral-800 transition-colors">
                        <td className="p-3 w-12">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePack(item.preset)}
                            className="h-6 w-6 p-0 hover:bg-white/10"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-white/60" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-white/60" />
                            )}
                          </Button>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 h-10 w-10 rounded-md overflow-hidden bg-neutral-700 flex items-center justify-center">
                              {item.presetCover ? (
                                <img
                                  src={item.presetCover}
                                  alt={item.preset}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/placeholder.svg';
                                  }}
                                />
                              ) : (
                                <span className="text-white/40 text-xs">Sem imagem</span>
                              )}
                            </div>
                            <div className="text-sm font-medium text-white">
                              {item.preset}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-white/70">{item.pack}</td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {totalUses.toLocaleString("pt-BR")}
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {item.paidUses.toLocaleString("pt-BR")}
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {item.freeUses.toLocaleString("pt-BR")}
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-white pr-6">
                          {Number(paidPercentage)}%
                        </td>
                      </tr>

                      {/* Expanded Row - Creators */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="bg-neutral-800/50 p-4 border-t border-white/5">
                              <div className="relative">
                                <div className="absolute left-4 top-0 bottom-0 w-px bg-white/5"></div>
                                <h4 className="text-sm font-medium text-white/80 mb-3 pl-8">
                                  Criadores deste preset:
                                </h4>
                                <div className="overflow-x-auto pl-8">
                                  <table className="w-full min-w-[400px] table-fixed">
                                    <thead className="bg-neutral-900/50">
                                      <tr>
                                        <th className="p-2 text-left text-xs font-medium text-white/60">Criador</th>
                                        <th className="p-2 text-right text-xs font-medium text-white/60">Porcentagem de Uso</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                      {item.creators.map((creator, index) => (
                                        <tr key={index} className="hover:bg-neutral-900/30 transition-colors">
                                          <td className="p-2">
                                            <div className="flex items-center space-x-3">
                                              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-neutral-700 flex items-center justify-center overflow-hidden">
                                                <span className="text-white/40 text-xs">
                                                  {creator.name.charAt(0)}
                                                </span>
                                              </div>
                                              <div className="text-xs font-medium text-white">
                                                {creator.name}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="p-2 text-right text-blue-400 font-medium text-xs">
                                            {creator.percentage}%
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Creator View */}
      {activeTab === 'creator' && (
        <div className="relative rounded-xl border border-white/10 overflow-hidden bg-neutral-900">
          {creatorMeta && (
            <div className="p-4 bg-neutral-800 text-sm text-white/60 border-b border-white/10">
              {[
                `Mostrando ${formatNumber(creatorData.length)} de ${formatNumber(creatorMeta.totalItems)} criadores`,
                `${formatNumber(creatorMeta.totalPaid)} usos pagos`,
                `${formatNumber(creatorMeta.totalFree)} usos gratuitos`,
                creatorMeta.totalClicks !== undefined && creatorMeta.totalClicks !== null
                  ? `${formatNumber(creatorMeta.totalClicks)} cliques`
                  : null,
                creatorMeta.clickPercentage !== undefined && creatorMeta.clickPercentage !== null
                  ? `${formatNumber(creatorMeta.clickPercentage)}% taxa de cliques`
                  : null,
                `${formatNumber(creatorMeta.paidPercentage)}% taxa de conversão`,
              ].filter(Boolean).join(' • ')}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[768px] table-fixed">
              <thead className="bg-neutral-800">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-white/80 w-12"></th>
                  <th className="p-3 text-left text-sm font-medium text-white/80">Nome</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Totais</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Pagos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Usos Gratuitos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">Cliques Totais</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">% Pagos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80">% Gratuitos</th>
                  <th className="p-3 text-right text-sm font-medium text-white/80 pr-6">% Cliques</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" key="creator-data">
                {creatorData.length === 0 && (
                  <tr key="no-data">
                    <td colSpan={9} className="p-3 text-center text-sm text-white/60">
                      Nenhum dado encontrado
                    </td>
                  </tr>
                )}

                {(creatorData ?? []).map((creator) => {
                  const isExpanded = expandedCreators[creator.creator] || false;
                  const paidUseValue = Number(creator.paidUse ?? 0);
                  const freeUseValue = Number(creator.freeUse ?? 0);
                  const totalUses = paidUseValue + freeUseValue;
                  const totalClicks = Number(creator.totalClicks ?? 0);

                  const paidPercentage = creator.paidPercentage !== undefined && creator.paidPercentage !== null
                    ? Number(creator.paidPercentage)
                    : (totalUses > 0 ? (paidUseValue / totalUses) * 100 : 0);
                  const freePercentage = creator.freePercentage !== undefined && creator.freePercentage !== null
                    ? Number(creator.freePercentage)
                    : (totalUses > 0 ? (freeUseValue / totalUses) * 100 : 0);
                  const clickPercentage = creator.clickPercentage !== undefined && creator.clickPercentage !== null
                    ? Number(creator.clickPercentage)
                    : 0;

                  return (
                    <>
                      <tr key={creator.creator} className="hover:bg-neutral-800 transition-colors">
                        <td className="p-3 w-12">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleCreator(creator.creator)}
                            className="h-6 w-6 p-0 hover:bg-white/10"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-white/60" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-white/60" />
                            )}
                          </Button>
                        </td>
                        <td className="p-3">
                          <div className="text-sm font-medium text-white">
                            {creator.creator}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {formatNumber(totalUses)}
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {formatNumber(paidUseValue)}
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {formatNumber(freeUseValue)}
                        </td>
                        <td className="p-3 text-sm text-right text-white/70">
                          {formatNumber(totalClicks)}
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-green-400">
                          {Number(paidPercentage).toFixed(2)}%
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-orange-400">
                          {Number(freePercentage).toFixed(2)}%
                        </td>
                        <td className="p-3 text-sm text-right font-semibold text-purple-400 pr-6">
                          {Number(clickPercentage).toFixed(2)}%
                        </td>
                      </tr>

                      {/* Expanded Row - Packs and Presets */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="p-0">
                            <div className="bg-neutral-800/50 p-4 border-t border-white/5">
                              <div className="relative">
                                <div className="absolute left-4 top-0 bottom-0 w-px bg-white/5"></div>
                                <h4 className="text-sm font-medium text-white/80 mb-3 pl-8">
                                  Packs e presets deste criador:
                                </h4>
                                <div className="space-y-4 pl-8">
                                  {Object.entries(creator.packs).map(([packName, presets]) => (
                                    <div key={packName} className="border border-white/10 rounded-lg p-3 bg-neutral-900/50">
                                      <h5 className="text-sm font-medium text-blue-400 mb-2">
                                        Pack: {packName}
                                      </h5>
                                      <div className="overflow-x-auto pl-8">
                                        <table className="w-full min-w-[500px] table-fixed">
                                          <thead className="bg-neutral-800/50">
                                            <tr>
                                              <th className="p-2 text-left text-xs font-medium text-white/60">Preset</th>
                                              <th className="p-2 text-right text-xs font-medium text-white/60">Usos Pagos</th>
                                              <th className="p-2 text-right text-xs font-medium text-white/60">Usos Gratuitos</th>
                                              <th className="p-2 text-right text-xs font-medium text-white/60">% Pago</th>
                                              <th className="p-2 text-right text-xs font-medium text-white/60">% Grátis</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-white/5">
                                            {Object.entries(presets).map(([presetName, presetData]) => (
                                              <tr key={presetName} className="hover:bg-neutral-800/30 transition-colors">
                                                <td className="p-2">
                                                  <div className="flex items-center space-x-3">
                                                    <div className="flex-shrink-0 h-8 w-8 rounded-md overflow-hidden bg-neutral-700 flex items-center justify-center">
                                                      {presetData.presetCover ? (
                                                        <img
                                                          src={presetData.presetCover}
                                                          alt={presetName}
                                                          className="h-full w-full object-cover"
                                                          onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = '/placeholder.svg';
                                                          }}
                                                        />
                                                      ) : (
                                                        <span className="text-white/40 text-xs">Sem imagem</span>
                                                      )}
                                                    </div>
                                                    <div className="text-xs font-medium text-white">
                                                      {presetName}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="p-2 text-right text-xs text-green-400">
                                                  {presetData.paidUse.toLocaleString("pt-BR")}
                                                </td>
                                                <td className="p-2 text-right text-xs text-orange-400">
                                                  {presetData.freeUse.toLocaleString("pt-BR")}
                                                </td>
                                                <td className="p-2 text-right text-xs text-green-400">
                                                  {Number(presetData.paidPercentage).toFixed(2)}%
                                                </td>
                                                <td className="p-2 text-right text-xs text-orange-400">
                                                  {Number(presetData.freePercentage).toFixed(2)}%
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 pb-6">
        <Pagination
          currentPage={currentPage || 1}
          totalPages={totalPages || 1}
          setCurrentPage={handlePageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={limit}
          totalItems={activeTab === 'pack'
            ? packMeta?.totalItems || 0
            : creatorMeta?.totalItems || 0}
          showPageSizeOptions={true}
        />
      </div>
    </div>
  );
}
